import { apiClient } from "./apiClient";
import useStore from "../utils/store";

// All PLM endpoints are scoped to the active workspace via ?workspaceId=
function plmClient(route, options) {
  const workspaceId = useStore.getState().workspaceId || localStorage.getItem("workspaceId");
  const separator = route.includes("?") ? "&" : "?";
  return apiClient(`${route}${separator}workspaceId=${encodeURIComponent(workspaceId)}`, options);
}

// ---------- Products ----------

export async function fetchProductsApi({ token }) {
  return plmClient("/plm/products", { token });
}

export async function fetchProductByIdApi({ token, productId }) {
  return plmClient(`/plm/products/${productId}`, { token });
}

export async function createProductApi({ token, product }) {
  return plmClient("/plm/products", { token, method: "POST", body: product });
}

export async function updateProductApi({ token, productId, product }) {
  return plmClient(`/plm/products/${productId}`, { token, method: "PUT", body: product });
}

export async function deleteProductApi({ token, productId }) {
  return plmClient(`/plm/products/${productId}`, { token, method: "DELETE" });
}

// ---------- Product Versions ----------

export async function fetchVersionsApi({ token, productId }) {
  return plmClient(`/plm/products/${productId}/versions`, { token });
}

export async function fetchVersionByIdApi({ token, productId, versionId }) {
  return plmClient(`/plm/products/${productId}/versions/${versionId}`, { token });
}

export async function createVersionApi({ token, productId, version }) {
  return plmClient(`/plm/products/${productId}/versions`, { token, method: "POST", body: version });
}

export async function updateVersionApi({ token, productId, versionId, version }) {
  return plmClient(`/plm/products/${productId}/versions/${versionId}`, { token, method: "PUT", body: version });
}

export async function deleteVersionApi({ token, productId, versionId }) {
  return plmClient(`/plm/products/${productId}/versions/${versionId}`, { token, method: "DELETE" });
}

// ---------- Version Entity Links ----------

export async function addEntityToVersionApi({ token, productId, versionId, entityType, data }) {
  return plmClient(`/plm/products/${productId}/versions/${versionId}/${entityType}`, { token, method: "POST", body: data });
}

export async function removeEntityFromVersionApi({ token, productId, versionId, entityType, entityId }) {
  return plmClient(`/plm/products/${productId}/versions/${versionId}/${entityType}/${entityId}`, { token, method: "DELETE" });
}

// ---------- Personas ----------

export async function fetchPersonasApi({ token, productId }) {
  const query = productId ? `?productId=${productId}` : "";
  return plmClient(`/plm/personas${query}`, { token });
}

export async function fetchPersonaByIdApi({ token, personaId }) {
  return plmClient(`/plm/personas/${personaId}`, { token });
}

export async function createPersonaApi({ token, persona }) {
  return plmClient("/plm/personas", { token, method: "POST", body: persona });
}

export async function updatePersonaApi({ token, personaId, persona }) {
  return plmClient(`/plm/personas/${personaId}`, { token, method: "PUT", body: persona });
}

export async function deletePersonaApi({ token, personaId }) {
  return plmClient(`/plm/personas/${personaId}`, { token, method: "DELETE" });
}

// ---------- Journey Maps ----------

export async function fetchJourneyMapsApi({ token, productId }) {
  const query = productId ? `?productId=${productId}` : "";
  return plmClient(`/plm/journeys${query}`, { token });
}

export async function fetchJourneyMapByIdApi({ token, journeyMapId }) {
  return plmClient(`/plm/journeys/${journeyMapId}`, { token });
}

export async function fetchJourneyMapsByPersonaApi({ token, personaId }) {
  return plmClient(`/plm/journeys/persona/${personaId}`, { token });
}

export async function createJourneyMapApi({ token, journeyMap }) {
  return plmClient("/plm/journeys", { token, method: "POST", body: journeyMap });
}

export async function updateJourneyMapApi({ token, journeyMapId, journeyMap }) {
  return plmClient(`/plm/journeys/${journeyMapId}`, { token, method: "PUT", body: journeyMap });
}

export async function deleteJourneyMapApi({ token, journeyMapId }) {
  return plmClient(`/plm/journeys/${journeyMapId}`, { token, method: "DELETE" });
}

// ---------- Journey Steps ----------

export async function createJourneyStepApi({ token, journeyMapId, step }) {
  return plmClient(`/plm/journeys/${journeyMapId}/steps`, { token, method: "POST", body: step });
}

export async function updateJourneyStepApi({ token, journeyMapId, stepId, step }) {
  return plmClient(`/plm/journeys/${journeyMapId}/steps/${stepId}`, { token, method: "PUT", body: step });
}

export async function deleteJourneyStepApi({ token, journeyMapId, stepId }) {
  return plmClient(`/plm/journeys/${journeyMapId}/steps/${stepId}`, { token, method: "DELETE" });
}

// ---------- Requirements ----------

export async function fetchRequirementTypesApi({ token }) {
  return plmClient("/plm/requirements/types", { token });
}

export async function createRequirementTypeApi({ token, name }) {
  return plmClient("/plm/requirements/types", { token, method: "POST", body: { name } });
}

export async function fetchRequirementsFlatApi({ token, productId }) {
  const query = productId ? `?productId=${productId}` : "";
  return plmClient(`/plm/requirements/flat${query}`, { token });
}

export async function fetchRequirementsApi({ token, productId }) {
  const query = productId ? `?productId=${productId}` : "";
  return plmClient(`/plm/requirements${query}`, { token });
}

export async function fetchRequirementByIdApi({ token, requirementId }) {
  return plmClient(`/plm/requirements/${requirementId}`, { token });
}

export async function createRequirementApi({ token, requirement }) {
  return plmClient("/plm/requirements", { token, method: "POST", body: requirement });
}

export async function updateRequirementApi({ token, requirementId, requirement }) {
  return plmClient(`/plm/requirements/${requirementId}`, { token, method: "PUT", body: requirement });
}

export async function deleteRequirementApi({ token, requirementId }) {
  return plmClient(`/plm/requirements/${requirementId}`, { token, method: "DELETE" });
}

// ---------- Requirement Coverage ----------

export async function addCoverageApi({ token, requirementId, journeyStepId }) {
  return plmClient(`/plm/requirements/${requirementId}/coverage`, { token, method: "POST", body: { journeyStepId } });
}

export async function removeCoverageApi({ token, requirementId, journeyStepId }) {
  return plmClient(`/plm/requirements/${requirementId}/coverage/${journeyStepId}`, { token, method: "DELETE" });
}

// ---------- Test Cases ----------

export async function fetchTestCasesApi({ token, productId }) {
  const query = productId ? `?productId=${productId}` : "";
  return plmClient(`/plm/test-cases${query}`, { token });
}

export async function fetchTestCaseByIdApi({ token, testCaseId }) {
  return plmClient(`/plm/test-cases/${testCaseId}`, { token });
}

export async function createTestCaseApi({ token, testCase }) {
  return plmClient("/plm/test-cases", { token, method: "POST", body: testCase });
}

export async function updateTestCaseApi({ token, testCaseId, testCase }) {
  return plmClient(`/plm/test-cases/${testCaseId}`, { token, method: "PUT", body: testCase });
}

export async function deleteTestCaseApi({ token, testCaseId }) {
  return plmClient(`/plm/test-cases/${testCaseId}`, { token, method: "DELETE" });
}

// ---------- Test Executions ----------

export async function fetchExecutionsApi({ token, testCaseId }) {
  return plmClient(`/plm/test-cases/${testCaseId}/executions`, { token });
}

export async function createExecutionApi({ token, testCaseId, execution }) {
  return plmClient(`/plm/test-cases/${testCaseId}/executions`, { token, method: "POST", body: execution });
}

// ---------- Tags ----------

export async function fetchTagsApi({ token }) {
  return plmClient("/plm/tags", { token });
}

export async function createTagApi({ token, name }) {
  return plmClient("/plm/tags", { token, method: "POST", body: { name } });
}

// ---------- Traceability ----------

export async function fetchTraceabilityReportApi({ token, productId, productVersionId }) {
  const params = new URLSearchParams();
  if (productId) params.set("productId", productId);
  if (productVersionId) params.set("productVersionId", productVersionId);
  const query = params.toString() ? `?${params}` : "";
  return plmClient(`/plm/traceability/report${query}`, { token });
}

export async function fetchCoverageSummaryApi({ token, productId, productVersionId }) {
  const params = new URLSearchParams();
  if (productId) params.set("productId", productId);
  if (productVersionId) params.set("productVersionId", productVersionId);
  const query = params.toString() ? `?${params}` : "";
  return plmClient(`/plm/traceability/coverage${query}`, { token });
}

// ---------- Audit ----------

export async function fetchAuditLogApi({ token, entityType, entityId, limit }) {
  if (entityType && entityId) {
    return plmClient(`/plm/audit/${entityType}/${entityId}`, { token });
  }
  return plmClient(`/plm/audit?limit=${limit || 100}`, { token });
}
